/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/client.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import axios from 'axios';
import { APIConfig } from '../../config/enums';
import { AUTH_STORAGE_KEYS } from '../../store/auth';
// API_BASE_URL is resolved by the canonical single source (services/api/config.ts).
// Re-exported here for backward-compat with existing `from '.../api/client'` imports.
import { API_BASE_URL, _tryRefreshToken as refreshTenantUserToken, clearSessionAndRedirectToLogin, redirectIfPasswordChangeRequired } from './config';

export { API_BASE_URL };
const API_VERSION = '/api/v1';

// Create axios instance
// S85L-T1: withCredentials sends httpOnly cookies automatically (TD-C11)
export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  timeout: APIConfig.DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // S85L-T1: Send httpOnly cookies on every request
});

// apiClientV2 — for /api/v2/* endpoints (Sprint 79L+)
// baseURL excludes /api/v1 prefix; components call apiClientV2.get("/api/v2/...")
// without path doubling.
export const apiClientV2 = axios.create({
  baseURL: API_BASE_URL,
  timeout: APIConfig.DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Shared auth/tenant header attachment
export const attachAuthAndTenant = (config: any) => {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Sprint 52 (S52-006): Read tenantKey from persisted auth store (CTO G1)
    // CTO F3 fix: Use AUTH_STORAGE_KEYS.ZUSTAND_STORE instead of hardcoded string
    // Pre-login requests (no token) skip X-Tenant-Key header
    const stored = localStorage.getItem(AUTH_STORAGE_KEYS.ZUSTAND_STORE);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const tenantKey = parsed?.state?.tenantKey;
        if (tenantKey) {
          config.headers['X-Tenant-Key'] = tenantKey;
        }
      } catch {
        // Ignore parse errors
      }
    }
  }
  return config;
};

apiClient.interceptors.request.use(attachAuthAndTenant, (err) => Promise.reject(err));
apiClientV2.interceptors.request.use(attachAuthAndTenant, (err) => Promise.reject(err));

// Token refresh function
//
// bot-066 (GH#884): tenant-user refresh now delegates to config.ts's
// _tryRefreshToken() singleton instead of POSTing /tenant-users/refresh
// independently. Root cause: this module's own isRefreshing/failedQueue
// guard only dedupes concurrent 401s WITHIN apiClient/apiClientV2 — it has
// no visibility into apiRequest/api/apiV2 (config.ts), which run their own
// separate single-flight guard for the SAME /tenant-users/refresh endpoint.
// useNotifications() (apiClient, this module) mounts on the exact same
// screens (AttendanceModern.tsx, EmployeeDashboard.tsx) as the attendance-
// history/my-schedule fetches (apiV2/apiRequest, config.ts). When the access
// token expires, BOTH stacks see 401 near-simultaneously and each reads the
// SAME refresh_token from localStorage and posts it independently. Backend
// refresh tokens are single-use (DD-0029 C2, backend/app/core/tenant_auth.py
// blacklist_token(old_jti) on first use) — whichever POST loses the race
// presents an already-blacklisted jti and gets a spurious 401, even though
// the session itself is fine. Sharing config.ts's in-flight promise collapses
// both stacks' concurrent refreshes into exactly one network call. The
// platform-admin branch is untouched: it uses a different token/endpoint and
// is not co-mounted with the config.ts stack, so it carries no such race.
const refreshAccessToken = async () => {
  try {
    // Sprint 54 (CTO F8): Route refresh to correct endpoint based on user type
    // Platform admins use /platform-admin/refresh, tenant users use /tenant-users/refresh
    const isPlatformAdmin = !!localStorage.getItem(AUTH_STORAGE_KEYS.PLATFORM_ADMIN_TOKEN);

    if (!isPlatformAdmin) {
      return await refreshTenantUserToken();
    }

    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await axios.post(
      `${API_BASE_URL}${API_VERSION}/platform-admin/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    // S85L-T1: Tokens are now set as httpOnly cookies by backend.
    // Keep localStorage write for backward compat during migration (remove in Sprint 86L).
    const { access_token, refresh_token: newRefreshToken } = response.data;
    if (access_token) {
      localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, access_token);
    }
    if (newRefreshToken) {
      localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
    }

    return access_token;
  } catch (error) {
    clearSessionAndRedirectToLogin(); // #1264
    throw error;
  }
};

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Shared 401 refresh response interceptor factory
// Applies to both apiClient and apiClientV2 — retries on the same instance
const createResponseInterceptor = (client: typeof apiClient) => {
  return client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // #1271: temporary password — server allows only change-password/logout/me
      redirectIfPasswordChangeRequired(error.response?.status, error.response?.data);

      // If 401 and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

createResponseInterceptor(apiClient);
createResponseInterceptor(apiClientV2);

export default apiClient;
